import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { selectCurrentUserData, selectToken } from "../store/auth/index";
import { useGetInvestorProfileQuery } from "../store/investor/investorAuthApi";
import { useGetInvestorKycQuery } from "../store/investor/investorKycApi";
import { canAccessMemberModule, normalizeUserType } from "../utils/investorAccess";
import { useUserType } from "./useUserType";

function getMemberInvestorUserId(userData) {
  if (!userData) return null;
  return `member_${userData.member_id || userData.id || userData.email || "unknown"}`;
}

function mapApiKycRecord(record) {
  if (!record) return null;
  return {
    legal_name: record.legal_name,
    nin_masked: record.nin_masked,
    status: record.status,
    match_status: record.match_status,
    submitted_at: record.submitted_at,
    reviewed_at: record.reviewed_at,
    rejection_reason: record.rejection_reason,
    resubmission_reason: record.resubmission_reason,
  };
}

/**
 * Active investor-module user context (real investor OR cooperative member).
 */
export function useInvestorSession() {
  const [sessionRevision, setSessionRevision] = useState(0);
  const memberUserData = useSelector(selectCurrentUserData);
  const memberToken = useSelector(selectToken);
  const userType = useUserType();

  const refresh = useCallback(() => {
    setSessionRevision((n) => n + 1);
  }, []);

  const isMember = canAccessMemberModule(userType) && Boolean(memberToken);
  const isRealInvestor = normalizeUserType(userType) === "investor" && Boolean(memberToken);
  const isRealModuleUser = (isRealInvestor || isMember) && Boolean(memberToken);

  const { data: apiProfile } = useGetInvestorProfileQuery(undefined, {
    skip: !isRealModuleUser,
  });

  const { data: apiKyc } = useGetInvestorKycQuery(undefined, {
    skip: !isRealModuleUser,
  });

  let activeUserId = null;
  let profile = null;
  let kycStatus = "none";
  let kycRecord = null;
  let onboardingProgress = null;
  let canTransact = false;
  let kycRequired = true;

  if (isRealInvestor || isMember) {
    const source = apiProfile || memberUserData;
    activeUserId = isMember && !isRealInvestor ? getMemberInvestorUserId(memberUserData) : source?.id;
    profile = {
      id: activeUserId,
      first_name: source?.first_name,
      last_name: source?.last_name,
      full_name:
        source?.name ||
        `${source?.first_name || ""} ${source?.last_name || ""}`.trim(),
      email: source?.email,
      phone: source?.phone,
      account_type: source?.account_type || (isMember && !isRealInvestor ? "member" : "investor"),
    };
    kycStatus = apiProfile?.kyc_status || apiKyc?.status || "none";
    kycRecord = mapApiKycRecord(apiKyc);
    kycRequired = apiProfile?.kyc_required !== false;
    canTransact = Boolean(apiProfile?.can_transact);
    onboardingProgress = {
      account: true,
      profile: Boolean(source?.profile_completed ?? true),
      kyc: kycStatus === "approved",
      welcome: kycStatus === "approved",
    };
  }

  return {
    isMember,
    isRealInvestor,
    isLoggedIn: isRealInvestor || isMember,
    activeUserId,
    profile,
    kycStatus,
    kycRecord,
    kycRequired,
    canTransact,
    isKycApproved: kycStatus === "approved",
    onboardingProgress,
    sessionRevision,
    refresh,
    userType: isRealInvestor ? "investor" : normalizeUserType(userType),
  };
}
